import React, { useState, useEffect } from 'react';
import { goBackOrHome } from '@/lib/navHelpers';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TherapyChat } from "@/components/TherapyChat";
import { TherappiDisclaimer } from "@/legal/components/TherappiDisclaimer";
import { SanctuaryHub } from "@/components/sanctuary/SanctuaryHub";
import { ConditionHub } from "@/components/sanctuary/ConditionHub";
import { SkillsLibrary } from "@/components/sanctuary/SkillsLibrary";
import { InTheMomentToolkit } from "@/components/sanctuary/InTheMomentToolkit";
import { PastSelfTimeline } from "@/components/sanctuary/PastSelfTimeline";
import { Heart, Shield, ArrowLeft, Brain, BookOpen, Sparkles, Zap, Clock } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDisclaimerConsent } from '@/hooks/useDisclaimerConsent';
import { AppHeader } from '@/components/AppHeader';
import { useContent } from '@/contexts/ContentContext';
import { useEditableContent } from '@/hooks/useEditableContent';

const Sanctuary = () => {
  const [user, setUser] = useState<User | null>(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [selectedMode, setSelectedMode] = useState<string>('general');
  const [activeTab, setActiveTab] = useState('ai-therapist');
  const navigate = useNavigate();
  const location = useLocation();
  
  // All hooks must be called at the top level
  const { hasTherapyConsent, acceptTherapyDisclaimer, loading: consentLoading } = useDisclaimerConsent(user?.id);
  
  // Editable content
  const welcomeTitle = useEditableContent('sanctuary.welcome.title', 'Welcome to The Sanctuary', 'Sanctuary');
  const welcomeDescription = useEditableContent('sanctuary.welcome.description', 'Your safe space for healing, growth, and support. Before we begin, please read and accept our important disclaimer.', 'Sanctuary');
  const welcomeButton = useEditableContent('sanctuary.welcome.button', 'Review Disclaimer & Enter', 'Sanctuary');
  const mainTitle = useEditableContent('sanctuary.main.title', 'The Sanctuary', 'Sanctuary');
  const mainDescription = useEditableContent('sanctuary.main.description', 'A comprehensive space for psychological healing and growth. Access AI-powered reflective support in multiple modes, condition-specific tools, evidence-based skills, and in-the-moment support.', 'Sanctuary');
  const badgeConfidential = useEditableContent('sanctuary.badge.confidential', '100% Confidential', 'Sanctuary');
  const badgeEvidence = useEditableContent('sanctuary.badge.evidence', 'Evidence-Based', 'Sanctuary');
  const badgeAI = useEditableContent('sanctuary.badge.ai', 'AI-Powered', 'Sanctuary');
  const tabAILabel = useEditableContent('sanctuary.tab.ai.label', 'AI THERAPPI<sup class="text-[0.6em]">®</sup>', 'Sanctuary');
  const tabAIShort = useEditableContent('sanctuary.tab.ai.short', 'AI', 'Sanctuary');
  const tabConditionsLabel = useEditableContent('sanctuary.tab.conditions.label', 'By Condition', 'Sanctuary');
  const tabConditionsShort = useEditableContent('sanctuary.tab.conditions.short', 'Tools', 'Sanctuary');
  const tabSkillsLabel = useEditableContent('sanctuary.tab.skills.label', 'Skills Library', 'Sanctuary');
  const tabSkillsShort = useEditableContent('sanctuary.tab.skills.short', 'Skills', 'Sanctuary');
  const tabToolkitLabel = useEditableContent('sanctuary.tab.toolkit.label', 'Quick Support', 'Sanctuary');
  const tabToolkitShort = useEditableContent('sanctuary.tab.toolkit.short', 'SOS', 'Sanctuary');
  const tabTimelineLabel = useEditableContent('sanctuary.tab.timeline.label', 'Past Self Timeline', 'Sanctuary');
  const tabTimelineShort = useEditableContent('sanctuary.tab.timeline.short', 'Timeline', 'Sanctuary');
  const disclaimerLink = useEditableContent('sanctuary.disclaimer.link', 'Review Sanctuary Disclaimer & Terms', 'Sanctuary');

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleAcceptDisclaimer = async () => {
    const success = await acceptTherapyDisclaimer();
    if (success) {
      setShowDisclaimer(false);
    }
  };

  const handleCancelDisclaimer = () => {
    setShowDisclaimer(false);
  };

  const handleSignOut = async () => {
    if (user) {
      await supabase.rpc('log_therapy_action', {
        p_user_id: user.id,
        p_action: 'user_logout',
        p_details: { timestamp: new Date().toISOString() }
      });
    }
    await supabase.auth.signOut();
  };

  if (!user || consentLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  const handleGoBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      goBackOrHome(navigate);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-therapeutic">
      <AppHeader user={user} onSignOut={handleSignOut} />

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto">
          <Button variant="ghost" onClick={handleGoBack} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {!hasTherapyConsent ? (
            <div className="space-y-6">
              <Card className="p-8 bg-gradient-therapeutic shadow-therapy border-0">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4 animate-pulse">
                    <Sparkles className="h-8 w-8 text-primary" />
                  </div>
                  <h2 className="text-3xl font-light text-foreground mb-3">{welcomeTitle}</h2>
                  <p className="text-muted-foreground mb-4 max-w-2xl mx-auto leading-relaxed">
                    {welcomeDescription}
                  </p>
                  <Button onClick={() => setShowDisclaimer(true)} className="bg-primary hover:bg-primary/90">
                    {welcomeButton}
                  </Button>
                </div>
              </Card>
            </div>
          ) : (
            <>
              {/* Welcome Header */}
              <Card className="mb-8 p-6 sm:p-8 bg-gradient-therapeutic shadow-therapy border-0 overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-soft-accent/5"></div>
                <div className="relative text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-4 animate-fade-in">
                    <Sparkles className="h-10 w-10 text-primary" />
                  </div>
                  <div className="inline-block px-3 py-1 mb-3 text-xs font-medium bg-primary/20 text-primary rounded-full">
                    PAST TIMELINE
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-light text-foreground mb-3">{mainTitle}</h2>
                  <p className="text-muted-foreground mb-4 max-w-3xl mx-auto leading-relaxed">
                    {mainDescription}
                  </p>
                  <p className="text-sm text-muted-foreground/80 max-w-2xl mx-auto italic mb-6">
                    Your Sanctuary is your <span className="text-primary">Past Timeline</span>. What you record here helps Therappi understand your history so your support can be more personalised.
                  </p>
                  
                  <div className="flex flex-wrap justify-center gap-4 text-sm">
                    <div className="flex items-center gap-2 text-foreground bg-background/50 backdrop-blur-sm px-4 py-2 rounded-full">
                      <Shield className="h-4 w-4 text-primary" />
                      <span>{badgeConfidential}</span>
                    </div>
                    <div className="flex items-center gap-2 text-foreground bg-background/50 backdrop-blur-sm px-4 py-2 rounded-full">
                      <Heart className="h-4 w-4 text-primary" />
                      <span>{badgeEvidence}</span>
                    </div>
                    <div className="flex items-center gap-2 text-foreground bg-background/50 backdrop-blur-sm px-4 py-2 rounded-full">
                      <Brain className="h-4 w-4 text-primary" />
                      <span>{badgeAI}</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Main Content Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="flex overflow-x-auto scrollbar-hide w-full mb-6 sm:mb-8 bg-gradient-calm border border-border/50 h-auto py-1.5 sm:py-2 px-1.5 gap-1 justify-start sm:justify-center">
                  <TabsTrigger value="ai-therapist" className="gap-1.5 sm:gap-2 text-sm sm:text-base py-2.5 sm:py-3 px-3 sm:px-4 shrink-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <Brain className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden sm:inline" dangerouslySetInnerHTML={{ __html: tabAILabel }} />
                    <span className="sm:hidden">{tabAIShort}</span>
                  </TabsTrigger>
                  <TabsTrigger value="conditions" className="gap-1.5 sm:gap-2 text-sm sm:text-base py-2.5 sm:py-3 px-3 sm:px-4 shrink-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <Heart className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden sm:inline">{tabConditionsLabel}</span>
                    <span className="sm:hidden">{tabConditionsShort}</span>
                  </TabsTrigger>
                  <TabsTrigger value="skills" className="gap-1.5 sm:gap-2 text-sm sm:text-base py-2.5 sm:py-3 px-3 sm:px-4 shrink-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <BookOpen className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden sm:inline">{tabSkillsLabel}</span>
                    <span className="sm:hidden">{tabSkillsShort}</span>
                  </TabsTrigger>
                  <TabsTrigger value="toolkit" className="gap-1.5 sm:gap-2 text-sm sm:text-base py-2.5 sm:py-3 px-3 sm:px-4 shrink-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <Zap className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden sm:inline">{tabToolkitLabel}</span>
                    <span className="sm:hidden">{tabToolkitShort}</span>
                  </TabsTrigger>
                  <TabsTrigger value="timeline" className="gap-1.5 sm:gap-2 text-sm sm:text-base py-2.5 sm:py-3 px-3 sm:px-4 shrink-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden sm:inline">{tabTimelineLabel}</span>
                    <span className="sm:hidden">{tabTimelineShort}</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="ai-therapist" className="mt-0">
                  <Card className="shadow-therapy border-0 overflow-hidden">
                    <TherapyChat user={user} selectedMode={selectedMode} onModeChange={setSelectedMode} />
                  </Card>
                </TabsContent>

                <TabsContent value="conditions" className="mt-0">
                  <ConditionHub user={user} />
                </TabsContent>

                <TabsContent value="skills" className="mt-0">
                  <SkillsLibrary user={user} />
                </TabsContent>

                <TabsContent value="toolkit" className="mt-0">
                  <InTheMomentToolkit user={user} />
                </TabsContent>

                <TabsContent value="timeline" className="mt-0">
                  <PastSelfTimeline user={user} />
                </TabsContent>
              </Tabs>

              {/* Disclaimer Link */}
              <div className="text-center mt-6">
                <Button 
                  variant="link" 
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setShowDisclaimer(true)}
                >
                  {disclaimerLink}
                </Button>
              </div>
            </>
          )}
        </div>

        <TherappiDisclaimer 
          isOpen={showDisclaimer}
          onAccept={handleAcceptDisclaimer}
          onCancel={handleCancelDisclaimer}
        />
      </main>
    </div>
  );
};

export default Sanctuary;
